/**
 * Les deux animateurs de « Écouter l'émission » et les ambiances.
 *
 * Module partagé (serveur et navigateur) : rien de secret ici. Les fiches de
 * personnalité partent dans le prompt qui écrit le dialogue ; les couleurs et
 * les initiales servent au lecteur.
 *
 * Une ambiance ne change qu'UNE ligne des fiches, la relation entre les deux.
 * Même article, mêmes voix, mêmes caractères : c'est ce qui rend le mode
 * « ils ne se supportent pas » drôle plutôt que différent.
 */

export const HOSTS = [
    {
        id: "margaux",
        name: "Margaux",
        initial: "M",
        // Pastille du lecteur : fond, texte, anneau quand elle parle.
        tint: "#FFE3DA",
        ink: "#C2410C",
        sheet: [
            "Margaux, la trentaine, a lu l'article en entier et l'a compris en profondeur.",
            "Vive, précise, ironique sans être méchante. Elle adore les exemples concrets et recadre dès que Bastien s'emballe.",
            "Elle rit facilement, souvent de Bastien, et elle a le sens de la formule.",
        ],
    },
    {
        id: "bastien",
        name: "Bastien",
        initial: "B",
        tint: "#DCEBFF",
        ink: "#1E3A5F",
        sheet: [
            "Bastien, la trentaine, découvre le sujet en même temps que l'auditeur et pose les questions qu'il se pose.",
            "Sûr de lui, un peu frimeur, enthousiaste. Il se reconnaît dans les pièges décrits par l'article, et finit par le dire.",
            "Il a de l'autodérision et ne déteste pas avoir le dernier mot.",
        ],
    },
];

export const AMBIANCES = [
    {
        id: "complices",
        relation:
            "Margaux et Bastien s'apprécient : ils s'écoutent, se relancent, se complètent et rient ensemble.",
    },
    {
        id: "rivaux",
        relation:
            "Margaux et Bastien ne se supportent pas : Margaux soupire et lève les yeux au ciel, Bastien lui coupe la parole, ils se chamaillent comme un vieux couple, sans jamais trahir le contenu de l'article.",
    },
];

export const DEFAULT_AMBIANCE = "complices";

/**
 * Le BeBot d'un animateur dans une ambiance : buste carré détouré (avatars)
 * ou en pied (choix d'ambiance). Générés le 26/10/2026 à partir de
 * public/images/bebot/coach/happy.webp, violets recalés sur les codes
 * officiels. Margaux : cils et nœud ; Bastien : mèche et lunettes relevées.
 */
export const portraitOf = (hostId, ambiance, { full = false } = {}) =>
    `/images/bebot/emissions/${hostId}-${isAmbiance(ambiance) ? ambiance : DEFAULT_AMBIANCE}${full ? "-full" : ""}.webp`;

export const isAmbiance = (id) => AMBIANCES.some((ambiance) => ambiance.id === id);

export const hostById = (id) => HOSTS.find((host) => host.id === id) || HOSTS[0];

/**
 * Voix Fish Audio par langue de l'article, dans l'ordre de HOSTS : l'indice
 * est celui de la balise <|speaker:N|> de Drama 3.
 *
 * Pour changer une voix : ouvrir sa page sur fish.audio et copier
 * l'identifiant à la fin de l'adresse. Préférer une voix enregistrée
 * proprement (sans souffle entre les phrases) : Drama 3 recopie le grain
 * de sa référence. Aucune voix de personne réelle, aucun clone.
 */
export const VOICES = {
    fr: {
        // Choisies à l'oreille par LN le 26/10/2026.
        // « Voix féminine journaliste » : claire, posée, expressive.
        margaux: "6ee0ebaa4c71419aa5937abd2db4da82",
        // « Yugo » : jeune homme, détendu et conversationnel.
        bastien: "5a6c59358bf045998201643ca0ecfa4b",
    },
    en: {
        // Delia et Adrian, les voix d'exemple de la documentation Drama 3.
        margaux: "c70cfb98867a4bfb9ae20d2401257b92",
        bastien: "bf322df2096a46f18c579d0baa36f41d",
    },
};

export const voicesFor = (locale) => VOICES[locale] || VOICES.fr;
